import * as Sentry from "@sentry/react";

const ignoreErrors = [
  // Ignore http://bit.ly/2hk42Ap, resulting from what
  // appears to be a Chrome bug http://bit.ly/2gVbncS
  /__gCrWeb\.autofill\.extractForms/,
  // Ignore http://bit.ly/2hk71Zv, resulting from
  // some really strange issue in Safari 9. Experienced
  // by Mozilla team, too http://bit.ly/2hk9vHq
  /docs-homescreen-gb-container/,
  // Ignore http://bit.ly/2mVGcgX. It's a weird react error.
  // Google says it _might_ be a logging error? http://bit.ly/2lEB3ZW
  /e\.getHostNode/,
  // Ignore http://bit.ly/2lyZSao, resulting from
  // some Chrome plugin presumably.
  /ahk_main/,
  // Some weird firefox errors that seem to happen only on firefox for iOS.
  // https://bugzilla.mozilla.org/show_bug.cgi?id=1394296
  /__firefox__\./,
];

function isCustomExclusion(event: any) {
  let ignore = false;

  if (event.exception) {
    const values = event.exception?.values || [];
    values.forEach((exception: any) => {
      ignoreErrors.forEach((error) => {
        ignore = ignore || exception?.value?.match(error) !== null;
      });
    });
  }

  return ignore;
}

function censorSensitiveInformation(event: any) {
  const keys = [
    "password",
    "secret",
    /token$/i,
    "credentials",
    // NOTE: `email`, `username`, and `ip_address` have been added since
    // noticing these identifiers in `event.user` -- these qualify as PII
    // https://engineering.zapier.com/guides/event-streaming/privacy-by-design/#what-is-personal-data
    // https://docs.sentry.io/platforms/javascript/enriching-events/identify-user/
    "email",
    "username",
    "ip_address",
  ];

  for (const eventKey in event) {
    const removeKey = keys.reduce(
      (result, key) => result || eventKey.match(key) !== null,
      false,
    );

    if (removeKey) {
      delete event[eventKey];
    } else if (typeof event[eventKey] === "object") {
      censorSensitiveInformation(event[eventKey]);
    }
  }
}

export default function initializeSentry({
  commitHash,
  dsn,
  environment,
}: {
  commitHash: string;
  dsn: string;
  environment: string;
}) {
  if (!commitHash.trim()) {
    // eslint-disable-next-line no-console
    console.info("Sentry not initialized: no `commitHash` provided.");
    return;
  }

  if (!dsn.trim()) {
    // eslint-disable-next-line no-console
    console.info("Sentry not initialized: no `dsn` provided.");
    return;
  }

  if (!environment.trim()) {
    // eslint-disable-next-line no-console
    console.info("Sentry not initialized: no `environment` provided.");
    return;
  }

  const isProduction = environment === "production";

  Sentry.init({
    dsn,

    environment,

    // Associate error events with a release version, so we can track which
    // release introduced new bugs. More info on Sentry releases:
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#release
    release: commitHash,

    // for Sentry.browserTracingIntegration -- 0.2 / 20% of `production`
    // transactions, 1.0 / 100% of `development` and `preview`
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#traces-sample-rate
    tracesSampleRate: isProduction ? 0.2 : 1.0,

    integrations: [
      // Enables performance metrics such as throughput, latency, and displaying
      // the impact of errors across multiple systems (read: Zapier services)
      // https://docs.sentry.io/platforms/javascript/configuration/integrations/browsertracing/
      Sentry.browserTracingIntegration(),

      // Extracts all non-native attributes from the Error object and attaches
      // them to the event as the `extra` data.
      // https://docs.sentry.io/platforms/javascript/configuration/integrations/extraerrordata/
      Sentry.extraErrorDataIntegration({ captureErrorCause: true }),

      // NOTE: Source Maps are shared with Sentry via the Gitlab Integration
      // https://zapier-1.sentry.io/settings/integrations/gitlab/118206/

      // NOTE: Sentry's Component Tracking is enabled via the `withProfiler`
      // higher-order component in `AppLayout` -- any components *not* nested
      // within an instance of `AppLayout` will not receive this same tracing
      // treatment unless the `withProfiler` HOC is applied to the layout. This
      // SDK feature requires enabling Sentry.browserTracingIntegration.
      // https://docs.sentry.io/platforms/javascript/guides/react/features/component-monitoring/
    ],

    beforeSend(event: Sentry.ErrorEvent) {
      if (isCustomExclusion(event)) return null;

      censorSensitiveInformation(event);

      if (!isProduction) {
        /* eslint-disable-next-line no-console */
        console.info("Sentry would have captured exception:", event);
        return null;
      }

      return event;
    },
  });
}
